import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { googleAPI } from '../../utils/api';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import { useToastContext } from '../../contexts/ToastContext';
import TechnicianSelectModal from '../TechnicianSelectModal/TechnicianSelectModal';
import { User, AlertTriangle, Users as UsersIcon, Star } from 'lucide-react';
import { anonymizeName, anonymizeCompanyName, anonymizeAddressString, censorReviewText } from '../../utils/demoMode';
import './GoogleReviews.scss';

const GoogleReviews = () => {
    const { showSuccess, showError, showWarning } = useToastContext();
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [accounts, setAccounts] = useState([]);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [locations, setLocations] = useState([]);
    const [selectedLocations, setSelectedLocations] = useState([]);
    const [syncedLocations, setSyncedLocations] = useState([]);
    const [reviews, setReviews] = useState([]);
    const [localReviews, setLocalReviews] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalReviews, setTotalReviews] = useState(0);
    const [activeTab, setActiveTab] = useState('manage'); // 'sync' or 'manage'
    const [error, setError] = useState('');
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [reviewToAssign, setReviewToAssign] = useState(null);
    const [groupedView, setGroupedView] = useState(true);
    const [businessGroups, setBusinessGroups] = useState([]);
    const [problematicReviewsCount, setProblematicReviewsCount] = useState(0);
    const [assignmentFilter, setAssignmentFilter] = useState(''); // '' | 'assigned' | 'unassigned'

    // Helper function to format and anonymize address
    const formatAddress = (storefrontAddress) => {
        if (!storefrontAddress) return null;
        const addressParts = [
            storefrontAddress.addressLines?.join(', '),
            storefrontAddress.locality,
            storefrontAddress.administrativeArea
        ].filter(Boolean);
        const fullAddress = addressParts.join(', ');
        return anonymizeAddressString(fullAddress);
    };

    const loadSyncedLocations = useCallback(async () => {
        try {
            const result = await googleAPI.getSyncedLocations();
            if (result.success) {
                setSyncedLocations(result.data);
            }
        } catch (error) {
            console.error('Error loading synced locations:', error);
        }
    }, []);

    const loadLocalReviews = useCallback(async (page = 1) => {
        try {
            const result = await googleAPI.getLocalReviews({ page, limit: 10 }); // Reduced limit for faster loading
            if (result.success) {
                const reviews = result.data.reviews;

                // Censor all review texts with AI
                const reviewsWithCensoredText = await Promise.all(
                    reviews.map(async (review) => ({
                        ...review,
                        censoredComment: await censorReviewText(review.comment)
                    }))
                );

                setLocalReviews(reviewsWithCensoredText);
                setCurrentPage(result.data.page);
                setTotalPages(result.data.totalPages);
                setTotalReviews(result.data.total);

                // Count problematic reviews
                const problematicCount = reviewsWithCensoredText.filter(review => {
                    const issue = review.metadata?.assignmentIssue;
                    return issue && (issue.type === 'multiple_techs' || issue.type === 'ambiguous_name');
                }).length;
                setProblematicReviewsCount(problematicCount);
            }
        } catch (error) {
            console.error('Error loading local reviews:', error);
        }
    }, []);

    const checkConnectionAndLoadData = useCallback(async () => {
        try {
            setLoading(true);
            setError('');

            await Promise.all([
                loadLocalReviews(),
                loadSyncedLocations()
            ]);

        } catch (error) {
            console.error('Error loading stored Google reviews:', error);
            setError(error.message || 'Failed to load stored Google reviews');
        } finally {
            setLoading(false);
        }
    }, [loadLocalReviews, loadSyncedLocations]);

    const openSyncTab = useCallback(async () => {
        setActiveTab('sync');
        if (accounts.length > 0) return;

        try {
            setError('');
            const testResult = await googleAPI.testConnection();
            if (!testResult.success || !testResult.authenticated) {
                setError(
                    'Direct Google provider sync is unavailable. '
                    + 'Reviews already stored for this location remain available.'
                );
                return;
            }

            const accountsResult = await googleAPI.getAccounts();
            if (accountsResult.success) {
                setAccounts(accountsResult.accounts || []);
                setSelectedAccount(null);
            }
        } catch (syncLoadError) {
            console.error('Error loading Google provider sync:', syncLoadError);
            setError(
                'Direct Google provider sync is unavailable. '
                + 'Reviews already stored for this location remain available.'
            );
        }
    }, [accounts.length]);

    const extractDomain = (websiteUrl) => {
        if (!websiteUrl) return null;
        try {
            const url = new URL(websiteUrl.startsWith('http') ? websiteUrl : `https://${websiteUrl}`);
            return url.hostname.replace('www.', '').toLowerCase();
        } catch {
            return null;
        }
    };

    const groupLocationsByBusiness = (locations) => {
        const groups = {};
        const websiteGroups = {};
        
        // First pass: Group by exact business name
        locations.forEach(location => {
            const businessName = location.title || location.displayName || 'Unknown Business';
            const website = location.websiteUri;
            const domain = extractDomain(website);
            
            // Track websites for potential grouping
            if (domain) {
                if (!websiteGroups[domain]) {
                    websiteGroups[domain] = [];
                }
                websiteGroups[domain].push(location);
            }
            
            if (!groups[businessName]) {
                groups[businessName] = {
                    businessName: businessName,
                    locations: [],
                    totalLocations: 0,
                    commonWebsite: website,
                    commonDomain: domain
                };
            }
            
            groups[businessName].locations.push(location);
            groups[businessName].totalLocations++;
        });
        
        // Second pass: Merge businesses that share the same domain (like VIVE)
        const finalGroups = {};
        const processedDomains = new Set();
        
        Object.values(groups).forEach(group => {
            const domain = group.commonDomain;
            
            // If this domain has multiple businesses, group them together
            if (domain && websiteGroups[domain]?.length > 1 && !processedDomains.has(domain)) {
                processedDomains.add(domain);
                
                // Create a parent company name based on the domain
                let parentCompanyName = domain;
                if (domain.includes('vivecollision.com')) {
                    parentCompanyName = 'VIVE Collision';
                } else if (domain.includes('a-1pc.com')) {
                    parentCompanyName = 'A-1 Pest Control';
                } else {
                    // Capitalize the first part of the domain
                    parentCompanyName = domain.split('.')[0].replace(/^\w/, c => c.toUpperCase());
                }
                
                // Collect all locations for this domain
                const allLocationsForDomain = websiteGroups[domain];
                
                finalGroups[parentCompanyName] = {
                    businessName: parentCompanyName,
                    locations: allLocationsForDomain,
                    totalLocations: allLocationsForDomain.length,
                    commonWebsite: group.commonWebsite,
                    commonDomain: domain,
                    isParentCompany: true
                };
            } else if (!domain || websiteGroups[domain]?.length === 1 || processedDomains.has(domain)) {
                // Keep as individual business if no domain or already processed
                if (!processedDomains.has(domain)) {
                    finalGroups[group.businessName] = group;
                }
            }
        });
        
        return Object.values(finalGroups).sort((a, b) => a.businessName.localeCompare(b.businessName));
    };

    const loadLocations = async (accountName) => {
        try {
            setLoading(true);
            const result = await googleAPI.getLocations(accountName);
            if (result.success) {
                const locations = result.locations || [];
                setLocations(locations);
                setSelectedLocations(
                    locations.filter((location) => location.assignedToCurrentLocation)
                );
                if (locations.length === 0) {
                    setError(
                        result.canManageMappings
                            ? 'The shared Google account returned no Business Profile locations for this account.'
                            : 'No Google Business locations from this account are assigned to the current Elevate location.'
                    );
                } else {
                    setError('');
                }
                
                // Create business groups
                const groups = groupLocationsByBusiness(locations);
                setBusinessGroups(groups);
            }
        } catch (error) {
            setError('Failed to load locations');
            console.error('Error loading locations:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAccountChange = async (accountName) => {
        if (!accountName) {
            setSelectedAccount(null);
            setSelectedLocations([]);
            setLocations([]);
            return;
        }

        const account = accounts.find(a => a.name === accountName);
        setSelectedAccount(account);
        setSelectedLocations([]);
        setLocations([]);
        
        if (account) {
            await loadLocations(account.name);
        }
    };

    const handleLocationToggle = (location) => {
        if (location.assignedElsewhere) return;
        setSelectedLocations(prev => {
            const isSelected = prev.some(loc => loc.name === location.name);
            if (isSelected) {
                return prev.filter(loc => loc.name !== location.name);
            } else {
                return [...prev, location];
            }
        });
    };

    const selectAllLocations = () => {
        setSelectedLocations(locations.filter((location) => !location.assignedElsewhere));
    };

    const clearLocationSelection = () => {
        setSelectedLocations([]);
    };

    const handleBusinessToggle = (businessGroup) => {
        const businessLocations = businessGroup.locations.filter((location) => !location.assignedElsewhere);
        const allBusinessLocationsSelected = businessLocations.every(loc => 
            selectedLocations.some(selectedLoc => selectedLoc.name === loc.name)
        );

        setSelectedLocations(prev => {
            if (allBusinessLocationsSelected) {
                // Remove all locations of this business
                return prev.filter(selectedLoc => 
                    !businessLocations.some(businessLoc => businessLoc.name === selectedLoc.name)
                );
            } else {
                // Add all locations of this business
                const newSelections = [...prev];
                businessLocations.forEach(businessLoc => {
                    if (!newSelections.some(selectedLoc => selectedLoc.name === businessLoc.name)) {
                        newSelections.push(businessLoc);
                    }
                });
                return newSelections;
            }
        });
    };

    const selectAllInBusiness = (businessGroup) => {
        setSelectedLocations(prev => {
            const newSelections = [...prev];
            businessGroup.locations
                .filter((location) => !location.assignedElsewhere)
                .forEach(location => {
                if (!newSelections.some(selectedLoc => selectedLoc.name === location.name)) {
                    newSelections.push(location);
                }
            });
            return newSelections;
        });
    };

    const isBusinessSelected = (businessGroup) => {
        return businessGroup.locations.every(loc => 
            selectedLocations.some(selectedLoc => selectedLoc.name === loc.name)
        );
    };

    const isBusinessPartiallySelected = (businessGroup) => {
        const selectedCount = businessGroup.locations.filter(loc => 
            selectedLocations.some(selectedLoc => selectedLoc.name === loc.name)
        ).length;
        return selectedCount > 0 && selectedCount < businessGroup.locations.length;
    };

    const syncReviews = async () => {
        if (!selectedAccount) {
            setError('Please select an account first');
            return;
        }

        if (selectedLocations.length === 0) {
            setError('Please select at least one location to sync');
            return;
        }

        try {
            setSyncing(true);
            setError('');

            const result = await googleAPI.syncReviews(selectedAccount.name, 1000, selectedLocations);
            
            if (result.success) {
                showSuccess(`Successfully synced ${result.data.summary.totalReviews} reviews from ${result.data.summary.totalLocations} locations!`);
                await loadLocalReviews(1);
                await loadSyncedLocations();
            } else {
                showError(result.message || 'Failed to sync reviews');
            }
        } catch (error) {
            console.error('Sync error:', error);
            
            // Try to get more detailed error information
            let errorMessage = 'Failed to sync reviews';
            if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error.message) {
                errorMessage = error.message;
            }
            
            setError(`Sync failed: ${errorMessage}`);
        } finally {
            setSyncing(false);
        }
    };

    const handleAssignClick = (review) => {
        setReviewToAssign(review);
        setShowAssignModal(true);
    };

    const handleAssignSubmit = async (technicianId) => {
        if (!reviewToAssign) return;
        
        try {
            const result = await googleAPI.assignReview(reviewToAssign.id, technicianId);
            if (result.success) {
                showSuccess(`Review assigned to technician ${technicianId} successfully!`);
                await loadLocalReviews(currentPage);
                await fetchProblematicReviewsStats(); // Refresh problematic reviews stats
            }
        } catch (error) {
            showError('Failed to assign review');
            console.error('Error assigning review:', error);
            throw error; // Re-throw so the modal knows there was an error
        }
    };

    const handleAssignModalClose = () => {
        setShowAssignModal(false);
        setReviewToAssign(null);
    };

    // Filter reviews based on assignment status
    const filteredReviews = useMemo(() => {
        if (!assignmentFilter) return localReviews;
        
        return localReviews.filter(review => {
            const hasAssignedTechnician = review.assignedTechnicianId || 
                                        (review.metadata && review.metadata.assignedTechnician);
            
            if (assignmentFilter === 'assigned') {
                return hasAssignedTechnician;
            } else if (assignmentFilter === 'unassigned') {
                return !hasAssignedTechnician;
            }
            
            return true;
        });
    }, [localReviews, assignmentFilter]);

    // Load data on component mount (placed after all function definitions)
    useEffect(() => {
        checkConnectionAndLoadData();
    }, [checkConnectionAndLoadData]);

    const renderSyncTab = () => (
        <div className="sync-tab">
            <h3>Sync Google Reviews</h3>
            
            {accounts.length > 0 ? (
                <div className="sync-controls">
                    <div className="form-group">
                        <label>Select Google Account:</label>
                        <select 
                            value={selectedAccount?.name || ''} 
                            onChange={(e) => handleAccountChange(e.target.value)}
                            className="form-control"
                        >
                            <option value="">Select an account...</option>
                            {accounts.map(account => (
                                <option key={account.name} value={account.name}>
                                    {anonymizeCompanyName(account.displayName || account.name)} ({account.type})
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Location Selection */}
                    {selectedAccount && locations.length > 0 && (
                        <div className="location-selection">
                            <h4>Select Businesses to Sync</h4>
                            <div className="location-controls">
                                <button 
                                    onClick={() => setGroupedView(!groupedView)}
                                    className="btn btn-outline-secondary btn-sm"
                                >
                                    {groupedView ? 'Individual View' : 'Business View'}
                                </button>
                                <button 
                                    onClick={selectAllLocations}
                                    className="btn btn-secondary btn-sm"
                                    disabled={selectedLocations.length === locations.length}
                                >
                                    Select All
                                </button>
                                <button 
                                    onClick={clearLocationSelection}
                                    className="btn btn-secondary btn-sm"
                                    disabled={selectedLocations.length === 0}
                                >
                                    Clear All
                                </button>
                                <span className="selection-count">
                                    {selectedLocations.length} of {locations.length} selected
                                </span>
                            </div>
                            
                            {groupedView ? (
                                <div className="business-groups-list">
                                    {businessGroups.map(businessGroup => (
                                        <div key={businessGroup.businessName} className="business-group">
                                            <div className="business-header">
                                                <label className="business-checkbox">
                                                    <input
                                                        type="checkbox"
                                                        checked={isBusinessSelected(businessGroup)}
                                                        ref={input => {
                                                            if (input) {
                                                                input.indeterminate = isBusinessPartiallySelected(businessGroup);
                                                            }
                                                        }}
                                                        onChange={() => handleBusinessToggle(businessGroup)}
                                                    />
                                                    <div className="business-info">
                                                        <div className="business-name">
                                                            <strong>{anonymizeCompanyName(businessGroup.businessName)}</strong>
                                                            <span className="location-count">
                                                                ({businessGroup.totalLocations} location{businessGroup.totalLocations !== 1 ? 's' : ''})
                                                            </span>
                                                        </div>
                                                    </div>
                                                </label>
                                                {businessGroup.totalLocations > 1 && (
                                                    <button 
                                                        onClick={() => selectAllInBusiness(businessGroup)}
                                                        className="btn btn-outline-primary btn-sm"
                                                        disabled={isBusinessSelected(businessGroup)}
                                                    >
                                                        Select All
                                                    </button>
                                                )}
                                            </div>
                                            
                                            {businessGroup.totalLocations > 1 && (
                                                <div className="business-locations">
                                                    {businessGroup.locations.map(location => (
                                                        <div key={location.name} className="location-item sub-location">
                                                            <label className="location-checkbox">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedLocations.some(loc => loc.name === location.name)}
                                                        disabled={location.assignedElsewhere}
                                                        onChange={() => handleLocationToggle(location)}
                                                    />
                                                                <div className="location-info">
                                                                    {businessGroup.isParentCompany ? (
                                                                        <div>
                                                                            <div className="location-business-name">
                                                                                {anonymizeCompanyName(location.title || location.displayName)}
                                                                            </div>
                                                                            <div className="location-address">
                                                                                {location.storefrontAddress ?
                                                                                    formatAddress(location.storefrontAddress) :
                                                                                    anonymizeAddressString(location.name)
                                                                                }
                                                                            </div>
                                                                        </div>
                                                                    ) : (
                                                                        <div className="location-address">
                                                                            {location.storefrontAddress ?
                                                                                formatAddress(location.storefrontAddress) :
                                                                                anonymizeAddressString(location.name)
                                                                            }
                                                                        </div>
                                                                    )}
                                                                    {location.assignedElsewhere && (
                                                                        <div className="location-address">Assigned to another Elevate location</div>
                                                                    )}
                                                                </div>
                                                            </label>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="locations-list">
                                    {locations.map(location => (
                                        <div key={location.name} className="location-item">
                                            <label className="location-checkbox">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedLocations.some(loc => loc.name === location.name)}
                                                    disabled={location.assignedElsewhere}
                                                    onChange={() => handleLocationToggle(location)}
                                                />
                                                <div className="location-info">
                                                    <div className="location-name">
                                                        {anonymizeCompanyName(location.title || location.displayName || location.name)}
                                                    </div>
                                                    {location.storefrontAddress && (
                                                        <div className="location-address">
                                                            {formatAddress(location.storefrontAddress)}
                                                        </div>
                                                    )}
                                                </div>
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {selectedAccount && (
                        <div className="sync-actions">
                            <button 
                                onClick={syncReviews}
                                disabled={syncing || locations.length === 0}
                                className="btn btn-primary"
                            >
                                {syncing
                                    ? 'Syncing...'
                                    : locations.length === 0
                                        ? 'No Business Locations Available'
                                        : 'Sync Selected Reviews'}
                            </button>
                            
                            {locations.length > 0 && (
                                <div className="locations-info">
                                    <p>Found {locations.length} location(s) for this account</p>
                                </div>
                            )}
                            
                            {selectedAccount.type === 'PERSONAL' && (
                                <div className="account-warning">
                                    <p><strong>Note:</strong> This is a personal Google account. To sync reviews, you need:</p>
                                    <ul>
                                        <li>A Google My Business account with verified business locations</li>
                                        <li>Business locations with customer reviews</li>
                                        <li>Proper Google My Business API access</li>
                                    </ul>
                                    <p>The sync may not work with personal accounts.</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            ) : (
                <div className="empty-state">
                    <AlertTriangle size={48} />
                    <h3>Google import is not connected</h3>
                    <p>
                        This only affects importing reviews directly from Google.
                        FieldRoutes appointments and review solicitations continue through the webhook.
                    </p>
                </div>
            )}
        </div>
    );

    const getReviewIssueInfo = (review) => {
        if (!review.metadata?.assignmentIssue) return null;
        
        const issue = review.metadata.assignmentIssue;
        switch (issue.type) {
            case 'multiple_techs':
                const techNames = issue.matchedTechnicians?.map(t => t.name).join(', ') || 'Unknown';
                return {
                    icon: <UsersIcon className="warning-icon" size={16} />,
                    tooltip: `Multiple technicians found in review: ${techNames}. Manual assignment required.`,
                    className: 'warning-multiple-techs',
                    detailText: techNames
                };
            case 'ambiguous_name':
                return {
                    icon: <AlertTriangle className="warning-icon" size={16} />,
                    tooltip: issue.description,
                    className: 'warning-ambiguous-name',
                    detailText: issue.description
                };
            case 'no_tech_found':
                return {
                    icon: <User className="warning-icon muted" size={16} />,
                    tooltip: 'No technician name found in review',
                    className: 'warning-no-tech',
                    detailText: 'No technician mentioned'
                };
            default:
                return null;
        }
    };

    const renderManageTab = () => (
        <div className="manage-tab">
            <h3>Manage Google Reviews</h3>
            
            {/* Assignment Filter */}
            <div className="filters-section">
                <div className="filter-group">
                    <label className="filter-label">Filter by Assignment:</label>
                    <select
                        className="filter-select"
                        value={assignmentFilter}
                        onChange={(e) => setAssignmentFilter(e.target.value)}
                    >
                        <option value="">All Reviews</option>
                        <option value="assigned">Assigned</option>
                        <option value="unassigned">Unassigned</option>
                    </select>
                </div>
                {assignmentFilter && (
                    <button 
                        className="clear-filter-btn"
                        onClick={() => setAssignmentFilter('')}
                        title="Clear filter"
                    >
                        Clear Filter
                    </button>
                )}
            </div>
            
            {/* Alert for problematic reviews */}
            {problematicReviewsCount > 0 && (
                <div className="problematic-reviews-alert">
                    <div className="alert-icon">
                        <AlertTriangle size={20} />
                    </div>
                    <div className="alert-content">
                        <div className="alert-title">Manual Assignment Required</div>
                        <div className="alert-message">
                            {problematicReviewsCount > 0 && (
                                <div className="page-count">
                                    <strong>{problematicReviewsCount}</strong> on this page.
                                </div>
                            )}
                            <div className="reasons">
                                Issues include:
                                <ul>
                                    <li>Multiple technicians mentioned in the same review</li>
                                    <li>Ambiguous names (multiple technicians with the same name)</li>
                                </ul>
                                Look for warning icons <UsersIcon size={14} className="inline-icon" /> and <AlertTriangle size={14} className="inline-icon" /> in the table below.
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {filteredReviews.length > 0 ? (
                <div className="reviews-table">
                    <div className="reviews-summary">
                        Showing {filteredReviews.length} of {localReviews.length} reviews
                        {assignmentFilter && ` (${assignmentFilter})`}
                    </div>
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Location</th>
                                <th>Customer</th>
                                <th>Rating</th>
                                <th>Review</th>
                                <th>Assigned To</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredReviews.map(review => {
                                const issueInfo = getReviewIssueInfo(review);
                                return (
                                    <tr key={review.id} className={issueInfo ? '' : ''}>
                                        <td>{new Date(review.createTime).toLocaleDateString()}</td>
                                        <td>{anonymizeCompanyName(review.locationTitle || 'N/A')}</td>
                                        <td>{anonymizeName(review.reviewerName)}</td>
                                        <td>
                                            <span className={`rating rating-${review.starRating}`}>
                                                {'★'.repeat(review.starRating)}
                                            </span>
                                        </td>
                                        <td className="review-text">{review.censoredComment || review.comment}</td>
                                        <td>
                                            <div className="assigned-cell">
                                                {review.assignedTechnician ?
                                                    anonymizeName(review.assignedTechnician.name) : 
                                                    <>
                                                        <span className="text-muted">Unassigned</span>
                                                        {issueInfo && (
                                                            <div className="assignment-warning-wrapper">
                                                                <div className={`assignment-warning ${issueInfo.className}`}>
                                                                    {issueInfo.icon}
                                                                </div>
                                                                <div className="warning-tooltip">
                                                                    <div className="tooltip-arrow"></div>
                                                                    <div className="tooltip-content">
                                                                        {issueInfo.tooltip}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </>
                                                }
                                            </div>
                                        </td>
                                        <td>
                                            <span className={`badge badge-${review.assignmentStatus}`}>
                                                {review.assignmentStatus}
                                            </span>
                                        </td>
                                        <td>
                                            {!review.assignedTechnician && (
                                                <button 
                                                    className="btn btn-sm btn-primary"
                                                    onClick={() => handleAssignClick(review)}
                                                >
                                                    Assign
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    
                    {totalPages > 1 && (
                        <div className="pagination">
                            <button 
                                onClick={() => loadLocalReviews(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="btn btn-sm btn-secondary"
                            >
                                Previous
                            </button>
                            
                            <span className="pagination-info">
                                Page {currentPage} of {totalPages} ({totalReviews} total reviews)
                            </span>
                            
                            <button 
                                onClick={() => loadLocalReviews(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className="btn btn-sm btn-secondary"
                            >
                                Next
                            </button>
                        </div>
                    )}
                </div>
            ) : (
                <p>
                    {assignmentFilter ? 
                        `No ${assignmentFilter} reviews found. ${localReviews.length > 0 ? 'Try changing the filter.' : 'Go to the Sync tab to import reviews.'}` :
                        'No Google reviews synced yet. Go to the Sync tab to import reviews.'
                    }
                </p>
            )}
        </div>
    );

    if (loading) {
        return <LoadingSpinner />;
    }

    return (
        <div className="google-reviews">
            <motion.div
                className="config-header"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
            >
                <div className="header-content">
                    <div className="logo-section">
                        <Star className="logo" />
                        <div>
                            <h1>Google Reviews Management</h1>
                            <p>Sync and manage Google Business reviews, assign to technicians, and monitor feedback</p>
                        </div>
                    </div>
                </div>
            </motion.div>

            {error && (
                <div className="alert alert-danger google-connection-alert">
                    <span>{error}</span>
                </div>
            )}

            {/* Display currently synced locations */}
            {syncedLocations.length > 0 && (
                <motion.div
                    className="synced-locations-section"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                >
                    <h3>Currently Synced Locations ({syncedLocations.length})</h3>
                    <div className="synced-locations-list">
                        {syncedLocations.map(location => (
                            <div key={location.id} className="synced-location-item">
                                <div className="location-name">
                                    {anonymizeCompanyName(location.locationTitle || location.googleLocationName)}
                                </div>
                                <div className="location-details">
                                    {location.locationAddress && (
                                        <span className="location-address">{anonymizeAddressString(location.locationAddress)}</span>
                                    )}
                                    <span className="sync-date">
                                        Last synced: {new Date(location.lastSyncAt).toLocaleDateString()}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </motion.div>
            )}

            <motion.div
                className="tabs-section"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
            >
                <div className="tabs">
                    <button
                        className={`tab ${activeTab === 'sync' ? 'active' : ''}`}
                        onClick={openSyncTab}
                    >
                        Sync Reviews
                    </button>
                    <button
                        className={`tab ${activeTab === 'manage' ? 'active' : ''}`}
                        onClick={() => {
                            setActiveTab('manage');
                            setError('');
                        }}
                    >
                        Manage Reviews ({totalReviews})
                    </button>
                </div>
            </motion.div>

            <div className="tab-content">
                {activeTab === 'sync' ? renderSyncTab() : renderManageTab()}
            </div>

            <TechnicianSelectModal
                isOpen={showAssignModal}
                onClose={handleAssignModalClose}
                onSubmit={handleAssignSubmit}
                title="Assign Review to Technician"
            />
        </div>
    );
};

export default GoogleReviews;
